import React, { ReactElement, useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import { useNavigate } from 'react-router-dom';
import { PoliciesService } from 'services';
import { IComplianceTemplate } from 'modules/policies/policy-management/policy-detail/components/Compliance';

export interface IPolicy {
  _id: string;
  title: string;
  description: string;
  content: string;
  version: string;
  owner: string;
  status: string;
  effectiveDate: string;
  publishedDate: any;
  approvedDate: any;
  nextReviewDate: any;
  updatedAt: any;
  acknowledgementRequired: boolean;
  policyMode: string;
  policyUrl: string;
  reviewDate: string;
  lastReviewedDate: any;
  reviewIntervalMonths: number;
  needsReview: boolean;
  isActive: boolean;
  deletedAt: any;
  deletedBy: any;
  deletionReason: any;
  createdAt: string;
  coverage: string;
  __v: number;
  frameworkIds: IComplianceTemplate[];
}

export interface IPolicySummary {
  totalPolicies: number;
  totalAcknowledgements: number;
  completedAcknowledgements: number;
  // pendingAcknowledgements: number;
  acknowledgementPercentage: number;
  assigedAcknowledgements: number;
}

interface InventoryRowProps {
  label: string;
  value: string | number;
  subText?: string;
}

const InventoryRow = ({
  label,
  value,
  subText,
}: InventoryRowProps): React.ReactElement => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="center"
    className="inventory-row"
    sx={{ py: 1 }}
  >
    <Stack
      direction="row"
      alignItems="center"
      gap={0.75}
      className="inventory-row__label-group"
    >
      <Typography
        className="inventory-row__label"
        sx={{ color: '#6b7280', fontSize: '14px' }}
      >
        {label}
      </Typography>
    </Stack>
    <Stack direction="column" alignItems="flex-end">
      <Typography
        className="inventory-row__value"
        sx={{ fontWeight: 600, color: '#052921', fontSize: '16px' }}
      >
        {value}
      </Typography>
      {subText && (
        <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>
          {subText}
        </Typography>
      )}
    </Stack>
  </Stack>
);

const PolicyManagement = (): ReactElement => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [summary, setSummary] = useState({
    totalPolicies: 0,
    totalAcknowledgements: 0,
    completedAcknowledgements: 0,
    // pendingAcknowledgements: 0,
    acknowledgementPercentage: 0,
    assigedAcknowledgements: 0,
  });
  // API Calls
  const loadRecords = (): void => {
    setIsLoading(true);
    const queryString = new URLSearchParams({
      offset: '0',
      limit: '1000',
      search: '',
    }).toString();
    PoliciesService.getAll(queryString)
      .then((response) => {
        const { data } = response.data;

        setSummary({ ...data });
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => setIsLoading(false));
  };

  // Hooks
  useEffect(() => {
    loadRecords();
  }, []);

  if (isLoading) {
    return (
      <Box className="empty-content">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Card variant="outlined">
        <CardHeader
          title="Policy Management"
          subheader="Manage, distribute, and track organisation-wide policies"
          action={
            <IconButton onClick={() => navigate('/policy-management')}>
              <RemoveRedEyeIcon />
            </IconButton>
          }
          sx={{
            '& .MuiCardHeader-title': {
              fontSize: '18px',
              fontWeight: 600,
              color: '#052921',
            },
            '& .MuiCardHeader-subheader': {
              fontSize: '14px',
              color: '#6b7280',
            },
          }}
        />

        <CardContent>
          <Box sx={{ mt: 3 }}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Stack spacing={1}>
                  <InventoryRow
                    label="Total Policies"
                    value={summary.totalPolicies}
                  />
                  <InventoryRow
                    label="Policy coverage"
                    value={`${summary.acknowledgementPercentage}%`}
                  />

                  <Box className="mt-10">
                    <LinearProgress
                      variant="determinate"
                      value={summary.acknowledgementPercentage}
                    />
                    <Typography component="span">
                      {summary.completedAcknowledgements} of{' '}
                      {summary.totalAcknowledgements} assignments completed
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Box>
        </CardContent>
      </Card>
    </>
  );
};

export default PolicyManagement;
